/**
 * Is this tab still running the build the server is serving?
 *
 * A single-page app never reloads itself. `Cache-Control: max-age=0,
 * must-revalidate` is honoured by a RELOAD — and a monitoring dashboard left
 * open on a site office screen does not get one for days. So after a deploy
 * the tab keeps executing yesterday's JavaScript against today's API, and the
 * symptoms look like product bugs: raw i18n keys where a label belongs, a
 * language switch that does nothing, a page that 404s its own lazy chunk.
 * That exact confusion cost a round-trip on 2026-09-02.
 *
 * The check compares the entry-module URL this tab BOOTED with against the one
 * the server is handing out right now. Vite fingerprints that filename with a
 * content hash, so "different URL" means "different build" with no build-time
 * plumbing, no version file to keep in sync, and nothing that can silently
 * drift. When either side can't be determined we report "not stale": a false
 * "please reload" banner is worse than a missed one.
 */

/** Entry-module URL declared in an index.html document, or null. */
export function entryFromHtml(html: string): string | null {
  for (const m of html.matchAll(/<script\b([^>]*)>/gi)) {
    const attrs = m[1];
    if (!/\btype\s*=\s*["']?module\b/i.test(attrs)) continue;
    const src = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(attrs);
    if (src) return src[1];
  }
  return null;
}

type ScriptQueryable = { querySelectorAll(sel: string): ArrayLike<{ getAttribute(n: string): string | null }> };

/** The entry-module URL THIS tab booted with, read off the live document. */
export function currentEntry(doc?: ScriptQueryable): string | null {
  const d = doc ?? (typeof document !== "undefined" ? (document as unknown as ScriptQueryable) : null);
  if (!d) return null;
  const nodes = d.querySelectorAll('script[type="module"][src]');
  const first = nodes.length ? nodes[0] : null;
  return first?.getAttribute("src") ?? null;
}

/**
 * The entry-module URL the server is serving right now.
 * Never throws: a null means "couldn't tell", not "unchanged".
 */
export async function deployedEntry(
  fetchImpl: typeof fetch = fetch,
  url = "/",
): Promise<string | null> {
  try {
    const res = await fetchImpl(url, { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    return entryFromHtml(await res.text());
  } catch {
    return null;
  }
}

/** True only when both sides are known AND they disagree. */
export async function isStale(
  current: string | null = currentEntry(),
  fetchImpl: typeof fetch = fetch,
  url = "/",
): Promise<boolean> {
  if (!current) return false;
  const served = await deployedEntry(fetchImpl, url);
  if (!served) return false;
  return served !== current;
}

/**
 * Did this error come from a lazy chunk that no longer exists?
 *
 * After a deploy the old bundle still asks for `shared-CKNrrDrl.js` by its old
 * hash. Once that file is gone the import rejects — which is how "I switched
 * the language and nothing happened" is produced. The recovery is a reload,
 * not a retry, so this is worth telling apart from an ordinary API failure.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : "";
  if (!msg) return false;
  return /dynamically imported module|module script failed|Unable to preload|ChunkLoadError|Loading chunk \d+ failed/i.test(
    msg,
  );
}

export interface VersionWatchOptions {
  onStale: () => void;
  /** Injected for tests; defaults to the real same-origin check. */
  check?: () => Promise<boolean>;
  /** Re-check cadence while the build is still current. */
  intervalMs?: number;
  /** Grace period after boot — never compete with the first paint. */
  firstDelayMs?: number;
}

/**
 * Poll until the build changes, fire `onStale` exactly once, then stop.
 * Returns a disposer. Firing more than once would be noise: the banner is
 * already up and the only cure is the reload the user hasn't taken yet.
 */
export function startVersionWatch({
  onStale,
  check = () => isStale(),
  intervalMs = 15 * 60 * 1000,
  firstDelayMs = 30 * 1000,
}: VersionWatchOptions): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const run = async () => {
    if (stopped) return;
    let stale = false;
    try {
      stale = await check();
    } catch {
      stale = false; // offline / blocked — say nothing
    }
    if (stopped) return;
    if (stale) {
      stopped = true;
      onStale();
      return;
    }
    timer = setTimeout(() => void run(), intervalMs);
  };

  timer = setTimeout(() => void run(), firstDelayMs);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
